import { Empty, Form, Result, Skeleton } from "antd";
import { RefundFlow } from "./RefundFlow";
import {
    type StrictlyBasisPoints,
    basisPointsFromDollars,
    strictlyZero,
    stringFromBasisPoints,
    stringFromDollars,
} from "@vyne-shared/utils/currency";
import { VyneModal, type VyneModalProps } from "@vyne-shared/components/vyneModal/VyneModal";
import { antdModalV5, create, useModal } from "@ebay/nice-modal-react";
import { getRefundAmountRemaining } from "./refundModalUtils";
import { reducer } from "./refundModal.reducer";
import { showHistoryModal } from "../Tabs/PatientActivity/PatientActivityTable/PatientMenu/HistoryModal/showHistoryModal";
import { useReducer } from "react";
import { useRefundSubmission } from "./useRefundSubmission";
import { useSharedTransactionInvalidation } from "utils/useSharedTransactionInvalidation";
import { useSingleTransactionHistory } from "api/clients/useSingleTransactionHistory";
import { useSubmitPartialRefunds } from "./useSubmitPartialRefunds";
import type { PatientHistoryResult, PaymentReportResult } from "api/responses";
import type { QueryState } from "@tanstack/react-query";
import type { RefundModalEvent } from "./refundModal.events";
import type { RefundResultState, TransactionRefundModalState } from "./refundModal.state";

interface RefundModalProps {
    /** The ID of the Patient/Responsible Party whose history we are displaying. */
    readonly patientId: number;

    /** The transaction ID that we are refunding. */
    readonly transactionId: number;

    /** Should onClose trigger event history modal? */
    readonly triggerEventHistory?: boolean;
}

export const RefundModal = create<RefundModalProps>(({ patientId, transactionId, triggerEventHistory }) => {
    const modal = useModal();
    const { afterClose, ...antdModalProps } = antdModalV5(modal);

    const [state, dispatch] = useReducer(reducer, { type: "transaction-selected", transactionId });

    const { isPending, isError, data } = useSingleTransactionHistory(patientId, transactionId);

    const { partialRefundForm, modalButtonProps } = useFormSubmission(patientId, transactionId, data, state, dispatch);

    useInvalidation(patientId, state);

    return (
        <VyneModal
            {...antdModalProps}
            afterClose={() => {
                afterClose();
                if (triggerEventHistory) {
                    void showHistoryModal(patientId);
                }
            }}
            className="refund-modal"
            width={700}
            title="Refund Patient"
            cancelText={triggerEventHistory ? "Back" : "Close"}
            {...modalButtonProps}
        >
            <Skeleton active loading={isPending}>
                {isError ? (
                    <Result
                        status="500"
                        title="Unable to get patient history"
                        subTitle="There was a problem retrieving patient history. Contact support if this error persists."
                    />
                ) : !data ? (
                    <Empty description="Transaction not found." />
                ) : (
                    <RefundFlow
                        partialRefundForm={partialRefundForm}
                        patientId={patientId}
                        transactionHistory={data}
                        state={state}
                        dispatch={dispatch}
                    />
                )}
            </Skeleton>
        </VyneModal>
    );
});

// ------------------------------------------------------
// Footer Components
// ------------------------------------------------------
function useFormSubmission(
    patientId: number,
    transactionId: number,
    data: PatientHistoryResult | undefined,
    state: TransactionRefundModalState,
    dispatch: React.Dispatch<RefundModalEvent>,
) {
    const { partialRefundForm, pending, submit } = useRefundSubmission(patientId, transactionId, dispatch);

    // Note that the AntD form internally stores amount as a string
    const partialRefundAmount = Form.useWatch("amount", partialRefundForm) as unknown as string;

    const partialEnabled = useSubmitPartialRefunds();

    const modalButtonProps =
        state.type === "refund-result"
            ? retryBtnProps(state, dispatch)
            : submitBtnProps(
                  submit,
                  partialEnabled,
                  data ? getRefundAmountRemaining(data) : strictlyZero,
                  partialRefundAmount,
                  pending,
              );

    return { partialRefundForm, modalButtonProps };
}

const retryBtnProps = (
    state: RefundResultState,
    dispatch: React.Dispatch<RefundModalEvent>,
): Partial<VyneModalProps> => {
    if (state.status === "error") {
        return {
            onOk: () => dispatch({ type: "cancel" }),
            okText: "Try Again",
        };
    }

    return {};
};

const submitBtnProps = (
    submit: () => Promise<void>,
    partialEnabled: boolean,
    maxRefundAmount: StrictlyBasisPoints,
    partialRefundAmount?: string,
    isLoading?: boolean,
): Partial<VyneModalProps> => {
    const refundDollars = parseFloat(partialRefundAmount ?? "") || 0;
    const refundBasisPoints = basisPointsFromDollars(refundDollars);

    return {
        okButtonProps: {
            disabled: partialEnabled && !validateRefundAmount(refundBasisPoints, maxRefundAmount),
            loading: isLoading,
        },
        onOk: () => void submit(),
        okText: `Refund ${partialEnabled ? stringFromDollars(refundDollars) : stringFromBasisPoints(maxRefundAmount)}`,
    };
};

function validateRefundAmount(partialRefundAmount: StrictlyBasisPoints, maxRefundAmount: StrictlyBasisPoints) {
    return partialRefundAmount > 0 && partialRefundAmount <= maxRefundAmount;
}

function useInvalidation(patientId: number, state: TransactionRefundModalState) {
    const stateAmount = state.type === "refund-result" ? state.amount : undefined;
    const stateStatus = state.type === "refund-result" ? state.status : undefined;
    const shouldInvalidate = state.type === "refund-result" && stateStatus === "success";

    // Invalidate queries that are affected by a partial or full refund.
    // Queries are invalidated immediately after success, so they can refresh in the background.
    useSharedTransactionInvalidation(
        "refund",
        patientId,
        state.transactionId,
        shouldInvalidate,
        (queryState: QueryState<PaymentReportResult>) => {
            if (queryState.data?.rows) {
                // if refund amount and patientId match the response from paymentReport, then stop refetching
                return queryState.data.rows.some(
                    (row) =>
                        stateAmount !== undefined &&
                        row.patientId === patientId &&
                        row.amount === stringFromBasisPoints(stateAmount as StrictlyBasisPoints),
                );
            }
            return false;
        },
    );
}
